import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  TableRow,
  TableCell,
  EmptyRow,
} from '../../../../../../components/customTableComponents';
import CheckboxCell from '../CheckboxCell/CheckboxCell';
import {
  Marks,
  IdAndCreationDate,
  Status,
  MasterAndExecuter,
  ParentServiceAndService,
  ExecutionDate,
  Address,
  InvoiceManager,
} from '../../../../../../components/requestComponents';
import ExpandedContent from './ExpandedContent';
import useChangeRequestStatusMutation from '../../../../mutations/useChangeRequestStatusMutation';
import useChangesMethodsForModals from '../../../../hooks/useChangesMethodsForModals';
import useFilterState from '../../../../hooks/useFilterState';
import { filtersData } from '../../../../constants/filtersData';
import { filterNames } from '../../../../constants/filterNames';
import styles from './Row.module.scss';

const statusIds = {
  closed: 3,
  cancelled: 4,
};

const noEditableStatusIds = new Set([statusIds.closed, statusIds.cancelled]);

const { RequestInvoiceBlock, requestInvoiceTypes } = InvoiceManager;

const Row = ({
  request,
  onSelectRowClick,
  selectedRows,
  onCompletedStatusClick,
  showInLifeCirle,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isSelected = (requestId) => selectedRows.indexOf(requestId) !== -1;

  const queryClient = useQueryClient();

  const [[fromDate, toDate]] = useFilterState(
    filtersData[filterNames.createDate]
  );

  const { getAndSetUpdatedRequest, getAndSetUpdatedDashboardRequest } =
    useChangesMethodsForModals();

  const { mutate: changeStatusMutate } = useChangeRequestStatusMutation({
    onSuccess: (_data, variables) => {
      getAndSetUpdatedRequest(variables.requestId);
      getAndSetUpdatedDashboardRequest(variables.requestId);
    },
  });

  const handleToggleExpanded = () => setExpanded((prev) => !prev);

  const commonCellProps = {
    verticalAlign: 'top',
    rowExpanded: expanded,
  };

  const handleStatusChange = (request, status) => {
    changeStatusMutate({ requestId: request.id, statusId: status.id });
  };

  const handleBindCallSuccess = () => {
    queryClient.setQueryData([`requests`, fromDate, toDate], (oldRequests) => {
      const newRequests = oldRequests.slice();

      const index = newRequests.findIndex(
        (newRequest) => newRequest.id === request.id
      );

      if (index) {
        newRequests[index].hasRecord = true;
      }

      return newRequests;
    });
  };

  const canChange = !noEditableStatusIds.has(request.statusId);

  return (
    <>
      <EmptyRow />
      <TableRow
        className={styles.row}
        onClick={handleToggleExpanded}
      >
        <CheckboxCell
          onChange={() => onSelectRowClick(request.id)}
          checked={isSelected(request.id)}
          verticalAlign="top"
        />
        <TableCell
          {...commonCellProps}
          rowSpan={expanded || request.isChargeable ? 2 : 1}
          position="left"
        >
          <Marks
            onClick={(e) => e.stopPropagation()}
            request={request}
            rowExpanded={expanded}
            onBindCallToRequestSucces={handleBindCallSuccess}
            isStatistics
          />
        </TableCell>
        <TableCell {...commonCellProps}>
          <IdAndCreationDate
            request={request}
            showInLifeCirle={showInLifeCirle}
          />
        </TableCell>
        <TableCell {...commonCellProps}>
          <Status
            request={request}
            onStatusChange={handleStatusChange}
            tooltipPlacement="topLeft"
            onCompletedStatusClick={onCompletedStatusClick}
            canChange={canChange}
          />
        </TableCell>
        <TableCell {...commonCellProps}>
          <MasterAndExecuter
            request={request}
            canChange={canChange}
          />
        </TableCell>
        <TableCell {...commonCellProps}>
          <ParentServiceAndService
            request={request}
            canChange={canChange}
          />
        </TableCell>
        <TableCell {...commonCellProps}>
          <ExecutionDate
            request={request}
            isStatistics
            canChange={canChange}
          />
        </TableCell>
        <TableCell
          {...commonCellProps}
          position="right"
        >
          <Address
            request={request}
            canChange={canChange}
          />
        </TableCell>
      </TableRow>
      {request.isChargeable && !expanded && (
        <TableRow
          onClick={handleToggleExpanded}
          className={styles.row}
        >
          <TableCell
            padding="empty"
            transparent
          />
          <TableCell
            colSpan={6}
            style={{ paddingTop: 0 }}
          >
            <RequestInvoiceBlock
              request={request}
              type={requestInvoiceTypes.otherPages}
              className={styles.invoiceCard}
              canChange={canChange}
            />
          </TableCell>
        </TableRow>
      )}
      {expanded && (
        <ExpandedContent
          request={request}
          canChange={canChange}
        />
      )}
    </>
  );
};

export default Row;
